import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../lib/api';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  subscriptionTier: 'free' | 'starter' | 'pro' | 'premium' | 'enterprise';
  subscriptionStatus: string;
  emailVerified: boolean;
  stellarPublicKey?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  // Actions
  setUser: (user: User) => void;
  setToken: (token: string | null) => void;
  login: (email: string, password: string) => Promise<void>;
  loginWithStellar: (signedTransaction: string) => Promise<void>;
  register: (email: string, password: string, firstName?: string, lastName?: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      isAuthenticated: false,

      setUser: (user) => set({ user, isAuthenticated: true }),

      setToken: (token: string | null) => {
        set({ token });
        if (token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
          delete api.defaults.headers.common['Authorization'];
        }
      },

      login: async (email, password) => {
        try {
          set({ isLoading: true });
          const response = await api.post('/auth/login', { email, password });
          const { user, tokens } = response.data.data;
          get().setToken(tokens.accessToken);
          set({ user, isAuthenticated: true });
        } catch (error: any) {
          throw new Error(error.response?.data?.error?.message || 'Login failed');
        } finally {
          set({ isLoading: false });
        }
      },

      loginWithStellar: async (signedTransaction) => {
        try {
          set({ isLoading: true });
          const response = await api.post('/auth/stellar/verify', {
            transaction: signedTransaction,
          });
          const { user, tokens } = response.data.data;
          get().setToken(tokens.accessToken);
          set({ user, isAuthenticated: true });
        } catch (error: any) {
          throw new Error(error.response?.data?.error?.message || 'Stellar login failed');
        } finally {
          set({ isLoading: false });
        }
      },

      register: async (email, password, firstName, lastName) => {
        try {
          set({ isLoading: true });
          const response = await api.post('/auth/register', {
            email,
            password,
            firstName,
            lastName,
          });
          const { user, tokens } = response.data.data;
          get().setToken(tokens.accessToken);
          set({ user, isAuthenticated: true });
        } catch (error: any) {
          throw new Error(error.response?.data?.error?.message || 'Registration failed');
        } finally {
          set({ isLoading: false });
        }
      },

      logout: () => {
        api.post('/auth/logout').catch(() => {});
        get().setToken(null);
        set({ user: null, isAuthenticated: false });
      },

      refreshUser: async () => {
        try {
          const response = await api.get('/auth/me');
          const { user } = response.data.data;
          set({ user });
        } catch {
          get().logout();
        }
      },

      updateUser: (updates) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        }));
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Initialize auth on app load
if (useAuthStore.getState().token) {
  api.defaults.headers.common['Authorization'] =
    `Bearer ${useAuthStore.getState().token}`;
  useAuthStore.getState().refreshUser();
}
